import { Room, RoomEvent, Track } from 'livekit-client';

export async function connectLiveKitAudioCall({ livekitUrl, livekitToken, onConnected, onDisconnected, onParticipantConnected, onParticipantDisconnected, onError, onScreenTracks, onScreenSharingChanged, video = false, signal, onReconnecting, onReconnected }) {
    const room = new Room({
        adaptiveStream: true,
        dynacast: true,
    });

    const handleConnected = () => onConnected?.();
    const handleDisconnected = (reason) => onDisconnected?.(reason);
    const handleParticipantConnected = (participant) => onParticipantConnected?.(participant);
    const handleParticipantDisconnected = (participant) => onParticipantDisconnected?.(participant);
    const handleError = (error) => onError?.(error);
    const audioElements = new Map();
    const assertActive = () => { if (signal && signal.aborted) throw new DOMException('Call cancelled', 'AbortError'); };
    const abortCall = () => { void room.disconnect(); };
    if (signal) signal.addEventListener('abort', abortCall);
    const handleReconnecting = () => onReconnecting?.();
    const handleReconnected = () => onReconnected?.();
    const updateScreens = () => {
        const tracks = [];
        room.remoteParticipants.forEach(participant => participant.trackPublications.forEach(publication => {
            if ([Track.Source.ScreenShare, Track.Source.Camera].includes(publication.source) && publication.track) tracks.push({ id: publication.trackSid, track: publication.track, name: participant.name || participant.identity, local: false });
        }));
        room.localParticipant.trackPublications.forEach(publication => {
            if ([Track.Source.ScreenShare, Track.Source.Camera].includes(publication.source) && publication.track) tracks.push({ id: publication.trackSid, track: publication.track, name: publication.source === Track.Source.ScreenShare ? 'Ваш экран' : 'Вы', local: true });
        });
        if (onScreenTracks) onScreenTracks(tracks);
        if (onScreenSharingChanged) onScreenSharingChanged(room.localParticipant.isScreenShareEnabled);
    };
    const attachTrack = (track) => {
        if (track.kind === Track.Kind.Audio && !audioElements.has(track.sid)) {
            const audio = track.attach(); audio.autoplay = true; audio.style.display = 'none';
            document.body.appendChild(audio); audioElements.set(track.sid, { track, audio });
            audio.play().catch(() => handleError(new Error('audio_playback_requires_gesture')));
        }
        updateScreens();
    };
    const detachTrack = track => {
        const entry = audioElements.get(track.sid);
        if (entry) { track.detach(entry.audio); entry.audio.remove(); audioElements.delete(track.sid); }
        updateScreens();
    };

    room
        .on(RoomEvent.Connected, handleConnected)
        .on(RoomEvent.Disconnected, handleDisconnected)
        .on(RoomEvent.ParticipantConnected, handleParticipantConnected)
        .on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected)
        .on(RoomEvent.MediaDevicesError, handleError)
        .on(RoomEvent.TrackSubscribed, attachTrack)
        .on(RoomEvent.TrackUnsubscribed, detachTrack)
        .on(RoomEvent.LocalTrackPublished, updateScreens)
        .on(RoomEvent.LocalTrackUnpublished, updateScreens)
        .on(RoomEvent.Reconnecting, handleReconnecting)
        .on(RoomEvent.Reconnected, handleReconnected);

    try {
        assertActive();
        await room.connect(livekitUrl, livekitToken, { autoSubscribe: true });
        assertActive();
        await room.startAudio();
        await room.localParticipant.setMicrophoneEnabled(true);
        assertActive();
        if (video) await room.localParticipant.setCameraEnabled(true);
        assertActive();
    } catch (error) {
        if (signal) signal.removeEventListener('abort', abortCall);
        audioElements.forEach(({ track, audio }) => { track.detach(audio); audio.remove(); });
        await room.disconnect();
        throw error;
    }

    return {
        room,
        disconnect: async () => {
            if (signal) signal.removeEventListener('abort', abortCall);
            room
                .off(RoomEvent.Connected, handleConnected)
                .off(RoomEvent.Disconnected, handleDisconnected)
                .off(RoomEvent.ParticipantConnected, handleParticipantConnected)
                .off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected)
                .off(RoomEvent.MediaDevicesError, handleError)
                .off(RoomEvent.TrackSubscribed, attachTrack)
                .off(RoomEvent.TrackUnsubscribed, detachTrack)
                .off(RoomEvent.LocalTrackPublished, updateScreens)
                .off(RoomEvent.LocalTrackUnpublished, updateScreens)
                .off(RoomEvent.Reconnecting, handleReconnecting)
                .off(RoomEvent.Reconnected, handleReconnected);
            audioElements.forEach(({ track, audio }) => { track.detach(audio); audio.remove(); });
            audioElements.clear();
            if (onScreenTracks) onScreenTracks([]);
            if (onScreenSharingChanged) onScreenSharingChanged(false);
            await room.localParticipant.setScreenShareEnabled(false).catch(() => undefined);
            await room.localParticipant.setMicrophoneEnabled(false).catch(() => undefined);
            await room.localParticipant.setCameraEnabled(false).catch(() => undefined);
            await room.disconnect();
        },
        setVideoEnabled: enabled => room.localParticipant.setCameraEnabled(enabled),
        setScreenSharing: async enabled => {
            if (enabled && !navigator.mediaDevices?.getDisplayMedia) throw new Error('screen_share_not_supported');
            await room.localParticipant.setScreenShareEnabled(enabled, { audio: true });
            updateScreens();
        },
        resumeAudio: () => room.startAudio(),
        setMuted: async (muted) => {
            await room.localParticipant.setMicrophoneEnabled(!muted);
        },
    };
}

export function createCallTone(kind = 'incoming') {
    let context = null;
    let timer = null;
    let stopped = false;

    const pattern = kind === 'ringback'
        ? { frequency: 440, duration: 0.55, gapMs: 2400, gain: 0.045 }
        : { frequency: 760, duration: 0.28, gapMs: 900, gain: 0.065 };

    const playPulse = () => {
        if (!context || stopped) return;
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.type = 'sine';
        oscillator.frequency.value = pattern.frequency;
        gain.gain.value = pattern.gain;
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.start();
        oscillator.stop(context.currentTime + pattern.duration);
    };

    return {
        async start() {
            if (stopped) return false;
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (!AudioContextClass) return false;

            try {
                context = context || new AudioContextClass();
                if (context.state === 'suspended') await context.resume();
                playPulse();
                timer = window.setInterval(playPulse, pattern.gapMs);
                return true;
            } catch {
                return false;
            }
        },
        stop() {
            stopped = true;
            if (timer) window.clearInterval(timer);
            timer = null;
            if (context) {
                context.close().catch(() => undefined);
                context = null;
            }
        },
    };
}
